import type { tTourStep } from '../Tour/Tour.types';
import useTour from '../Tour/useTour.hook';
import { CHALLENGE_TOURS } from './ChallengeCenter.tours';
import type { tChallengeKey } from './ChallengeCenter.types';

const NO_STEPS: never[] = [];

// Cleanup is best effort: a selector that stopped matching must not throw
// its way out of the exit and leave the tour on screen.
function runCleanup(onEnd?: () => void): void {
  try {
    onEnd?.();
  } catch {
    // The modal it failed to close is still closable by hand.
  }
}

export type tChallengeTourEvents = {
  // The tour reached its end with at least one step shown: mark practiced.
  onComplete: () => void;
  // The tour ran out before its last step (those targets never rendered)
  // and the person did not finish it themselves: tell them where it stopped
  // instead of vanishing.
  onEndedEarly?: (stepsShown: number, totalSteps: number) => void;
  // A step resolved its target and its coachmark is showing. Steps that
  // auto-skip because their target never rendered do not report.
  onStepEnter?: (step: tTourStep, index: number) => void;
  // The person closed the tour (Saltar or Escape) on the step it reports.
  onSkip?: (exitStepId: string | null, exitStepIndex: number) => void;
};

/**
 * Looks up the guided-tour steps for a challenge key from the shared
 * registry and drives the real Tour engine (SAD D14: the tour stops one
 * step short of the irreversible action, `onComplete` is what marks
 * `practiced_at`, never the action itself). Every way out — Listo, Saltar,
 * Escape, an early end — runs the tour's `onEnd`, which closes whatever the
 * tour opened on the person's behalf.
 */
export function useChallengeTour(key: tChallengeKey | null, events: tChallengeTourEvents) {
  const config = key ? CHALLENGE_TOURS[key] : undefined;
  return useTour(config?.steps ?? NO_STEPS, {
    // `stepsShown` counts only the steps that resolved a target; useTour
    // auto-skips the ones whose target never renders and still reaches its
    // onComplete branch. Marking `practiced_at` off that path would claim
    // the person was shown a tour they never saw — which is exactly what a
    // stale selector produces, silently and unrecoverably.
    onComplete: (stepsShown, totalSteps, endedByAction, lastStepShown) => {
      // Ended because the person did the last step's action themselves
      // (pressed Guardar, sent the invite): what the tour opened is now
      // theirs, closing it would undo their work.
      if (!endedByAction) runCleanup(config?.onEnd);
      if (stepsShown > 0) events.onComplete();
      // "La guía llega hasta aquí" only when the tour ran out before its end:
      // its last step never showed and the person did not finish it by doing
      // the action. A middle step that auto-skipped (the owner field of the
      // Lead Agent stage editor) is not an early end (AGI-498).
      if (stepsShown > 0 && !endedByAction && !lastStepShown) events.onEndedEarly?.(stepsShown, totalSteps);
    },
    onStepEnter: events.onStepEnter,
    onSkip: (exitStepId, exitStepIndex) => {
      runCleanup(config?.onEnd);
      events.onSkip?.(exitStepId, exitStepIndex);
    },
  });
}
